import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Bell, Mail, Loader2 } from 'lucide-react';

const NotificationSettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    email_on_submission: true,
    send_customer_confirmation: false,
    customer_confirmation_template: '',
  });

  useEffect(() => {
    fetchSettings();
  }, [user]);

  const fetchSettings = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setSettings({
          email_on_submission: data.email_on_submission,
          send_customer_confirmation: data.send_customer_confirmation,
          customer_confirmation_template: data.customer_confirmation_template || '',
        });
      }
    } catch (error: any) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('notification_settings')
        .upsert({
          user_id: user.id,
          ...settings,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Notification settings saved successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Notification Settings</h1>
          <p className="text-muted-foreground">Configure how you want to be notified about form submissions</p>
        </div>

        <div className="grid gap-6 max-w-2xl">
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                Submission Notifications
              </CardTitle>
              <CardDescription>Get notified when someone submits one of your forms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email-notifications">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive email when someone submits a form</p>
                </div>
                <Switch
                  id="email-notifications"
                  checked={settings.email_on_submission}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, email_on_submission: checked })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Respondent Confirmations
              </CardTitle>
              <CardDescription>Automatically send confirmation emails to respondents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="customer-confirmation">Send Confirmation Emails</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically email respondents when they submit
                  </p>
                </div>
                <Switch
                  id="customer-confirmation"
                  checked={settings.send_customer_confirmation}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, send_customer_confirmation: checked })
                  }
                />
              </div>

              {settings.send_customer_confirmation && (
                <div className="space-y-2 animate-in fade-in duration-300">
                  <Label htmlFor="template">Confirmation Email Template</Label>
                  <Textarea
                    id="template"
                    placeholder="Thank you for your submission! We've received your information and will get back to you soon."
                    value={settings.customer_confirmation_template}
                    onChange={(e) =>
                      setSettings({ ...settings, customer_confirmation_template: e.target.value })
                    }
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    This message will be sent to respondents after they submit a form
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Button onClick={handleSave} disabled={isSaving} size="lg" className="w-full">
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Settings
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NotificationSettings;
